import { getTenantContext } from "./tenantConfig";
import { supabase } from "./supabaseClient";

const DEFAULT_FAVICON = `${process.env.PUBLIC_URL || ""}/favicon.png`;

let cachedBranding = {
  tenantId: "",
  title: "LMS",
  faviconUrl: DEFAULT_FAVICON,
  name: "",
  shortName: "",
  website: "",
  logoUrl: null,
  uen: "",
  address: "",
  postalCode: "",
  email: "",
  phone: "",
  contactPhone: "",
  whatsapp: "",
  signatoryName: "",
  signatoryDesignation: "",
  bankName: "",
  bankBranchCode: "",
  bankSwiftCode: "",
  bankAccountType: "",
  bankAccountNumber: "",
  bankBeneficiaryName: "",
};

/**
 * Show LMS bank-transfer option only when org bank details are set in TMS.
 * Same idea as Stripe: hide the payment method when not configured.
 */
export const isBankTransferConfigured = (branding = cachedBranding) => {
  const accountNumber = (branding?.bankAccountNumber || "").trim();
  const bankName = (branding?.bankName || "").trim();
  return Boolean(accountNumber && bankName);
};

const titleCaseTenantId = (tenantId) => {
  if (!tenantId) return "LMS";
  return tenantId.charAt(0).toUpperCase() + tenantId.slice(1);
};

export const buildTenantDocumentTitle = (pageTitle) => {
  const base = cachedBranding.title || "LMS";
  if (!pageTitle) return base;
  return `${pageTitle} - ${base}`;
};

export const getTenantBranding = () => cachedBranding;

export const applyTenantDocumentTitle = (pageTitle) => {
  document.title = buildTenantDocumentTitle(pageTitle);
};

const applyFavicon = (faviconUrl) => {
  const href = faviconUrl || DEFAULT_FAVICON;
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "icon");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);

  let apple = document.querySelector("link[rel='apple-touch-icon']");
  if (!apple) {
    apple = document.createElement("link");
    apple.setAttribute("rel", "apple-touch-icon");
    document.head.appendChild(apple);
  }
  apple.setAttribute("href", href);
};

const readLmsContact = (lmsContact) => {
  const lms = lmsContact && typeof lmsContact === "object" ? lmsContact : {};
  return {
    contactPhone: lms.phone || "",
    whatsapp: lms.whatsapp || lms.phone || "",
    signatoryName: lms.signatory_name || "",
    signatoryDesignation: lms.signatory_designation || "",
  };
};

/**
 * Load org branding from Supabase and apply tab title + favicon.
 * Call only after initializeSupabaseClient().
 * Fallback: tenant slug title (e.g. "Lumax LMS") + default /favicon.png
 * Text fields stay blank if missing (no invented labels).
 */
export const loadAndApplyTenantBranding = async () => {
  const { tenantId } = getTenantContext();
  const fallbackTitle = `${titleCaseTenantId(tenantId)} LMS`;

  cachedBranding = {
    ...cachedBranding,
    tenantId,
    title: fallbackTitle,
  };

  try {
    const { data, error } = await supabase
      .from("organization")
      .select(
        "name, short_name, logo_url, favicon_url, website, uen, address, postal_code, email, phone, lms_contact, bank_name, bank_branch_code, bank_swift_code, bank_account_type, bank_account_number, bank_beneficiary_name"
      )
      .eq("status", "active")
      .limit(1)
      .maybeSingle();

    if (error) throw error;

    if (data) {
      const lms = readLmsContact(data.lms_contact);
      const orgName = data.name || "";
      cachedBranding = {
        tenantId,
        title: fallbackTitle,
        faviconUrl: data.favicon_url || DEFAULT_FAVICON,
        name: orgName,
        shortName: data.short_name || "",
        website: data.website || "",
        logoUrl: data.logo_url || null,
        uen: data.uen || "",
        address: data.address || "",
        postalCode: data.postal_code || "",
        email: data.email || "",
        phone: data.phone || "",
        contactPhone: lms.contactPhone || data.phone || "",
        whatsapp: lms.whatsapp || data.phone || "",
        signatoryName: lms.signatoryName,
        signatoryDesignation: lms.signatoryDesignation,
        bankName: data.bank_name || "",
        bankBranchCode: data.bank_branch_code || "",
        bankSwiftCode: data.bank_swift_code || "",
        bankAccountType: data.bank_account_type || "",
        bankAccountNumber: data.bank_account_number || "",
        bankBeneficiaryName: data.bank_beneficiary_name || orgName || "",
      };
    }
  } catch (error) {
    console.error("[tenantBranding] Failed to load organization branding:", error);
    cachedBranding = {
      ...cachedBranding,
      tenantId,
      title: fallbackTitle,
      faviconUrl: DEFAULT_FAVICON,
    };
  }

  applyTenantDocumentTitle();
  applyFavicon(cachedBranding.faviconUrl);
  return cachedBranding;
};
